import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * iOS grouped-list building blocks for the mobile settings: a small grey
 * section header, a borderless rounded card whose rows are split by
 * hairlines inset from the left (see .settings-card in index.css), and an
 * optional grey footer.
 */
export const SettingsGroup = ({
  title,
  footer,
  children,
}: {
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-1.5">
    {title ? (
      <h3 className="px-4 text-[15px] font-normal text-[color:var(--settings-secondary)]">
        {title}
      </h3>
    ) : null}
    <div className="settings-card overflow-hidden rounded-[22px] bg-[var(--settings-card)]">
      {children}
    </div>
    {footer ? (
      <p className="px-4 text-[13px] leading-snug text-[color:var(--settings-secondary)]">
        {footer}
      </p>
    ) : null}
  </section>
);

/** Shared look of a 52px+ row; exported for rows rendered by other triggers. */
export const settingsRowClass =
  "flex min-h-[52px] w-full items-center gap-3 px-4 py-2.5 text-left text-[17px] text-[color:var(--settings-label)] outline-none transition-colors focus-visible:bg-[var(--settings-pressed)] active:bg-[var(--settings-pressed)] motion-reduce:transition-none";

type SettingsRowProps = {
  label: ReactNode;
  /** Leading glyph, shown in a small navy tile like iOS settings icons. */
  icon?: ReactNode;
  /** Grey line under the label. */
  secondary?: ReactNode;
  /** Grey value on the right, before the chevron. */
  value?: ReactNode;
  /** Extra control on the right (icon, select…). */
  trailing?: ReactNode;
  chevron?: boolean;
  /** Disclosure state: rotates the chevron and sets aria-expanded. */
  expanded?: boolean;
  destructive?: boolean;
  to?: string;
  onClick?: () => void;
  className?: string;
};

export const SettingsRow = ({
  label,
  icon,
  secondary,
  value,
  trailing,
  chevron,
  expanded,
  destructive,
  to,
  onClick,
  className,
}: SettingsRowProps) => {
  const hasIcon = icon ? true : undefined;
  const body = (
    <>
      {icon ? (
        <span
          aria-hidden
          className="flex size-[1.875rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-[#0B2545] text-white dark:bg-[#1d3a63]"
        >
          {icon}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {secondary ? (
          <span className="truncate text-[15px] text-[color:var(--settings-secondary)]">
            {secondary}
          </span>
        ) : null}
      </span>
      {value != null && value !== "" ? (
        <span className="max-w-[60%] shrink-0 truncate text-[color:var(--settings-secondary)]">
          {value}
        </span>
      ) : null}
      {trailing}
      {chevron ? (
        <ChevronRight
          aria-hidden
          className={cn(
            "size-5 shrink-0 text-[color:var(--settings-chevron)] transition-transform motion-reduce:transition-none",
            expanded && "rotate-90",
          )}
          strokeWidth={2.25}
        />
      ) : null}
    </>
  );
  const classes = cn(
    settingsRowClass,
    destructive &&
      "justify-center text-center text-[color:var(--settings-destructive)] [&>span]:flex-none",
    className,
  );

  if (to) {
    return (
      <Link to={to} className={classes} data-has-icon={hasIcon}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        data-has-icon={hasIcon}
        onClick={onClick}
        aria-expanded={expanded}
      >
        {body}
      </button>
    );
  }
  return (
    <div className={classes} data-has-icon={hasIcon}>
      {body}
    </div>
  );
};
